import React, { useState, useCallback, useEffect } from 'react';
import Sidebar from '../Sidebar';
import Header from './Header';

// Feature views — each is self-contained with its own data, modals, CRUD
import CustomersView from '../features/customers/CustomersView';
import ItemsView from '../features/items/ItemsView';
import CategoriesView from '../features/categories/CategoriesView';
import SuppliersView from '../features/suppliers/SuppliersView';
import TaxesView from '../features/taxes/TaxesView';
import InvoicesView from '../features/invoices/InvoicesView';
import BillsView from '../features/bills/billsView';
import PurchasesView from '../features/purchases/PurchasesView';
import QuotesView from '../features/quotes/QuotesView';
import DashboardView from '../features/dashboard/DashboardView';
import SalesByCustomerView from '../features/reports/SalesByCustomerView';
import SalesByItemView from '../features/reports/SalesByItemView';
import PurchaseBySupplierView from '../features/reports/PurchaseBySupplierView';
import ValuationView from '../features/reports/ValuationView';
import OrganisationSettingsView from '../features/settings/OrganisationSettingsView';
import { QuickBooksStatusProvider } from '../../context/QuickBooksStatusContext';
import { XeroStatusProvider } from '../../context/XeroStatusContext';

/**
 * Main application layout — the thin orchestrator.
 *
 * Before: 565 lines — owned ALL data fetching, CRUD operations for 6 entities,
 *         modal state management, search, rendering logic, 5 repeated switch
 *         statements, and inline styling.
 *
 * After:  ~55 lines — just layout (sidebar + header + content area) and view routing.
 *         Each feature view is self-contained and manages its own concerns.
 *
 * SOLID principles applied:
 *  - Single Responsibility: Dashboard only handles layout + navigation
 *  - Open/Closed: adding a new entity = add one entry to VIEW_MAP, no other changes
 *  - Dependency Inversion: Dashboard doesn't know about Supabase or any data layer
 */

const VIEW_MAP = {
  dashboard:  { title: 'Dashboard', component: DashboardView },
  customers:  { title: 'Customers', component: CustomersView },
  items:      { title: 'Items',             component: ItemsView },
  category:   { title: 'Categories',        component: CategoriesView },
  supplier:   { title: 'Suppliers',         component: SuppliersView },
  tax:        { title: 'Tax Codes',         component: TaxesView },
  invoices:   { title: 'Invoices',          component: InvoicesView },
  quotes:     { title: 'Quotes',            component: QuotesView },
  bills:      { title: 'Bills',             component: BillsView },
  purchases: { title: 'Purchase Orders', component: PurchasesView },
  organisationSettings:   { title: 'Settings', component: OrganisationSettingsView },
  'sales-by-customer': { title: 'Sales by Customer', component: SalesByCustomerView },
  'sales-by-item':     { title: 'Sales by Item',     component: SalesByItemView },
  'purchase-by-supplier': { title: 'Purchase by Supplier', component: PurchaseBySupplierView },
  'valuation':         { title: 'Stock Valuation',   component: ValuationView },
};

function getInitialActiveView() {
  if (typeof window === 'undefined') return 'dashboard';
  const params = new URLSearchParams(window.location.search);
  if (params.get('quickbooks') === 'connected') {
    // Set synchronously so a dev Strict Mode remount still opens Settings after the URL is stripped.
    sessionStorage.setItem('qb_return_to_settings', '1');
    return 'organisationSettings';
  }
  if (params.get('xero') === 'connected') {
    sessionStorage.setItem('xero_return_to_settings', '1');
    return 'organisationSettings';
  }
  if (sessionStorage.getItem('qb_return_to_settings') === '1') {
    return 'organisationSettings';
  }
  if (sessionStorage.getItem('xero_return_to_settings') === '1') {
    return 'organisationSettings';
  }
  return 'dashboard';
}

export default function Dashboard({ session, onLogout }) {
  const [activeView, setActiveView] = useState(getInitialActiveView);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [importToInvoiceData, setImportToInvoiceData] = useState(null);
  const [importToBillData, setImportToBillData] = useState(null);

  // After accounting OAuth, the API redirects here with ?quickbooks=connected or ?xero=connected
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quickbooks') === 'connected' || params.get('xero') === 'connected') {
      setActiveView('organisationSettings');
      window.history.replaceState({}, '', window.location.pathname || '/');
    }
    const clearFlag = setTimeout(() => {
      sessionStorage.removeItem('qb_return_to_settings');
      sessionStorage.removeItem('xero_return_to_settings');
    }, 500);
    return () => clearTimeout(clearFlag);
  }, []);

  const handleImportToInvoice = useCallback((quoteData) => {
    setImportToInvoiceData(quoteData);
    setActiveView('invoices');
  }, []);

  const handleImportToBill = useCallback((purchaseData) => {
    setImportToBillData(purchaseData);
    setActiveView('bills');
  }, []);

  const current = VIEW_MAP[activeView];
  const ViewComponent = current?.component;

  return (
    <QuickBooksStatusProvider navigationEpoch={activeView}>
    <XeroStatusProvider navigationEpoch={activeView}>
    <div className="flex h-screen w-screen overflow-hidden bg-[#f9fafb]">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        activeView={activeView}
        onNavigate={setActiveView}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title={current?.title || 'Dashboard'} onLogout={onLogout} />

        <div className="flex-1 p-6 overflow-hidden flex flex-col box-border">
          {ViewComponent ? (
            <ViewComponent
              {...(activeView === 'quotes' ? { onImportToInvoice: handleImportToInvoice } : {})}
              {...(activeView === 'invoices' ? {
                importFromQuote: importToInvoiceData,
                onClearImport: () => setImportToInvoiceData(null),
              } : {})}
              {...(activeView === 'purchases' ? { onImportToBill: handleImportToBill } : {})}
              {...(activeView === 'bills' ? {
                importFromPurchase: importToBillData,
                onClearImport: () => setImportToBillData(null),
              } : {})}
            />
          ) : (
            <div>
              <h2>{activeView.charAt(0).toUpperCase() + activeView.slice(1)} view under construction</h2>
            </div>
          )}
        </div>
      </div>
    </div>
    </XeroStatusProvider>
    </QuickBooksStatusProvider>
  );
}
